import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Mic } from "lucide-react";
import AudioService from "../services/audioService3";
import DeepgramTranscriptionService from "../services/deepgramService";
import ExitExamDialog from "@/components/models/ExitExamDialog";
import { useAudioWave } from "@/hooks/waveData";
import logo from "./../../public/images/logo.png";
import { Button } from "@/components/ui/button";
import { getSessionId, getToken, isGuestUser } from "@/utils/storage";
import "./task1.css";
import { getStoredGuestExamUser } from "@/api/exam";
import { checkSubscriptionAccess } from "@/utils/subscriptionCheck";
import { isTaskDeliberateTrial, getExamMode } from "@/utils/examMode";
import {
  isLiveExamRuntimeExiting,
  markLiveExamRuntimeExiting,
} from "@/lib/speaking/exam/live/liveExamRuntimeTeardown";
import {
  claimTask3TopicReadout,
  scheduleTask3InstructionToTopicPause,
  shouldReleaseTask3TopicReadout,
  TASK3_INSTRUCTION_TO_TOPIC_PAUSE_MS,
} from "@/lib/speaking/exam/live/task3InstructionToTopicPause";
import { markSpeakingLatency } from "@/lib/speaking/exam/live/speakingLatencyTelemetry";

type StaticType = 'intro' | 'start_prep' | 'start_exam_3' | 'task_completed' | 'trail-topic';
const STATIC_AUDIO: Array<{ type: StaticType; url: string; text: string }> = [
  {
    type: 'intro',
    text: "Tâche 3. Vous allez maintenant donner votre avis sur un sujet sans préparation. Vous avez quatre minutes et trente secondes pour vous exprimer librement. Voici votre sujet.",
    url: 'https://nluntjftnyzlxhatvvks.supabase.co/storage/v1/object/public/static-audio/task3-introfr.mp3'
  },
  {
    type: 'start_exam_3',
    text: "Lorsque vous êtes prêt, vous pouvez commencer.",
    url: 'https://nluntjftnyzlxhatvvks.supabase.co/storage/v1/object/public/static-audio/task3-start-exam-fr.mp3'
  },

  {
    type: 'task_completed',
    text: "Merci. Ceci conclut cette partie de l'examen",
    url: 'https://nluntjftnyzlxhatvvks.supabase.co/storage/v1/object/public/static-audio/task3-end-fr.mp3'
  },
  {
    type: 'trail-topic',
    text: "Certains pensent que les réseaux sociaux rapprochent les gens, d’autres qu’ils les isolent. Quelle est votre opinion ?",
    url: `https://nluntjftnyzlxhatvvks.supabase.co/storage/v1/object/public/static-audio/task3-trail-audio.mp3`,
  }
];
const getStaticByType = (type: StaticType) => STATIC_AUDIO.find(a => a.type === type);
// Small async delay helper (used to prevent audio overlap issues)
const wait = (ms: number) =>
  new Promise(resolve => setTimeout(resolve, ms));


// Fluency analysis is now handled on the completed screen

const TaskThree = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const locationExamId = location.state?.examId;
  const audioServiceRef = useRef<AudioService | null>(null);
  const speechRecognitionRef = useRef<DeepgramTranscriptionService | null>(null);

  // UI State
  const [showPreStartMessage, setShowPreStartMessage] = useState(true);
  const [isRecording, setIsRecording] = useState(false);
  const [showExitModal, setShowExitModal] = useState(false);
  const [remainingSeconds, setRemainingSeconds] = useState(270); // 4:30
  const [examStarted, setExamStarted] = useState(false);
  const [introText, setIntroText] = useState([]);

  // Audio and Connection State
  const [status, setStatus] = useState("Ready to start");
  const [isConnected, setIsConnected] = useState(false);
  const [fullTranscript, setFullTranscript] = useState("");
  const [liveTranscript, setLiveTranscript] = useState("");
  // initial AI intro for Task 3
  const [aiQuestions, setAiQuestions] = useState<string[]>([]);
  const [isAISpeaking, setIsAISpeaking] = useState(false);
  const [isUserSpeaking, setIsUserSpeaking] = useState(false);
  const [userHasResponded, setUserHasResponded] = useState(false);
  const [timerActive, setTimerActive] = useState(false);
  const [showDivider, setShowDivider] = useState(false);
  const [topicText, setTopicText] = useState<string>("");
  const [isFinishingTask, setIsFinishingTask] = useState(false);
  const isWaveActive = isRecording && isUserSpeaking;
  const messagesRef = useRef<HTMLDivElement | null>(null);
  const introFinishedRef = useRef(false);
  const hasShownIntro = useRef(false);
  const pendingTopicRef = useRef<{ text: string; audio?: string } | null>(null);
  const topicReadoutClaimedRef = useRef(false);
  const instructionToTopicPauseElapsedRef = useRef(false);
  const cancelInstructionToTopicPauseRef = useRef<(() => void) | null>(null);
  const task3UnmountedRef = useRef(false);
  const isRetryAttempt = localStorage.getItem('current_task_attempt_id');

  useEffect(() => {
    markSpeakingLatency({ task: 'task3', marker: 'T3_ROUTE_MOUNT' });
  }, []);

  // Check subscription access on mount
  useEffect(() => {
    const checkAccess = async () => {
      const deliberateTrial = isTaskDeliberateTrial({
        hasLocationExamId: !!locationExamId,
        hasCurrentExamId: !!localStorage.getItem("current_exam_id"),
        examMode: getExamMode(),
      });
      const result = await checkSubscriptionAccess({ deliberateTrial });
      if (!result.hasAccess) {
        console.log('❌ Task 3: Subscription access denied -', result.reason);
        window.location.href = '/subscription';
      }
    };
    checkAccess();
  }, [navigate]);

  // Handle examId from location state for retry attempts
  useEffect(() => {
    if (locationExamId) {
      localStorage.setItem("current_exam_id", locationExamId);
      console.log('🎯 TASK 3 - Set examId from location state for retry:', locationExamId);
    }
  }, [locationExamId]);
  // Auto-scroll to bottom when new AI questions arrive
  useEffect(() => {
    if (!messagesRef.current) return;
    if (introText.length > 0 && !hasShownIntro.current) {
      hasShownIntro.current = true;
      return;
    }
    messagesRef.current.scrollTo({
      top: messagesRef.current.scrollHeight,
      behavior: "smooth",
    });
  }, [aiQuestions, introText]);

  // Health check for voice detection - ensure it stays active during exam
  useEffect(() => {
    if (!examStarted || !isRecording) return;

    const healthCheckInterval = setInterval(() => {
      if (speechRecognitionRef.current && typeof speechRecognitionRef.current.isActive === 'function') {
        const isActive = speechRecognitionRef.current.isActive();
        const isHealthy = speechRecognitionRef.current.isHealthy?.() ?? true;

        if (!isActive || !isHealthy) {
          console.log("🔄 Voice detection not active/healthy, restarting...");
          speechRecognitionRef.current.start();
        } else {
          // Extra check for last 30 seconds on mobile
          if (remainingSeconds < 30) {
            console.log("✅ Voice detection is active and healthy (last 30 seconds)");
          } else {
            console.log("✅ Voice detection is active and healthy");
          }
        }
      }
    }, 1000); // Check every 1 second for more aggressive monitoring

    return () => clearInterval(healthCheckInterval);
  }, [examStarted, isRecording, remainingSeconds]);

  // Mobile-specific touch handling for better interruption
  useEffect(() => {
    if (!examStarted || !isRecording) return;

    const handleTouchStart = () => {
      // Resume audio context on touch (mobile requirement)
      const ctx = (window as any).__appAudioContext;
      if (ctx && ctx.state === 'suspended') {
        ctx.resume().catch(() => { });
      }

      // Ensure microphone is active on touch
      if (speechRecognitionRef.current && typeof speechRecognitionRef.current.isActive === 'function') {
        const isActive = speechRecognitionRef.current.isActive();
        if (!isActive) {
          console.log("🔄 Touch detected - restarting voice detection");
          speechRecognitionRef.current.start();
        }
      }
    };

    // Add touch event listeners for mobile
    document.addEventListener('touchstart', handleTouchStart, { passive: true });
    document.addEventListener('touchend', handleTouchStart, { passive: true });

    return () => {
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchend', handleTouchStart);
    };
  }, [examStarted, isRecording]);

  // Special handling for last 30 seconds on mobile
  useEffect(() => {
    if (!examStarted || !isRecording || remainingSeconds >= 30) return;

    console.log("📱 Last 30 seconds - ensuring mobile microphone stays active");

    // Force restart voice detection in last 30 seconds
    const lastPhaseInterval = setInterval(() => {
      if (speechRecognitionRef.current && typeof speechRecognitionRef.current.isActive === 'function') {
        const isActive = speechRecognitionRef.current.isActive();
        if (!isActive) {
          console.log("🔄 Last 30 seconds - restarting voice detection on mobile");
          speechRecognitionRef.current.start();
        }
      }
    }, 2000); // Check every 2 seconds in last 30 seconds

    return () => clearInterval(lastPhaseInterval);
  }, [examStarted, isRecording, remainingSeconds]);

  const waveData = useAudioWave({
    active: isRecording,
    bars: 24,
    gain: 4.2,
    smoothing: 0.5,
    exponent: 0.45,
  });

  // Hide pre-start message after 3 seconds, then start exam flow
  useEffect(() => {
    const timer = setTimeout(() => {
      const isRetryAttempt = !!localStorage.getItem('current_task_attempt_id');
      const sessionId = getSessionId();
      if (!sessionId && !isRetryAttempt) {
        console.warn('[TASK-3] No sessionId found and not a retry - redirecting to dashboard to prevent direct access');
        window.location.href = '/dashboard';
        return;
      }
      handleStartExam();
    }, 3000);

    return () => clearTimeout(timer);
  }, []);

  const handleAIResponse = (response: { text: string; audio?: string }) => {
    setAiQuestions(prev => {
      const incoming = (response.text || '').trim();
      const last = (prev[prev.length - 1] || '').trim();
      if (incoming && incoming === last) return prev;
      return [...prev, response.text];
    });
    setIsAISpeaking(true);
    setUserHasResponded(false);
  };
  const remainingSecondsRef = useRef(270);

  // Timer countdown (270 -> 0)
  useEffect(() => {
    console.log("🕐 Task 3 Timer useEffect triggered, timerActive:", timerActive);
    if (!timerActive) return;

    console.log("⏰ Task 3: Starting timer countdown from", remainingSecondsRef.current);
    const interval = setInterval(() => {
      remainingSecondsRef.current -= 1;
      setRemainingSeconds(remainingSecondsRef.current);

      if (remainingSecondsRef.current <= 0) {
        clearInterval(interval);
        audioServiceRef.current?.triggerEndOnce('timer_zero');
        setIsAISpeaking(true);
      }

      if (remainingSecondsRef.current === 45) {
        setStatus("Final 45 seconds: silence will end exam early.");
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [timerActive]);

  // Update audio service with remaining seconds
  useEffect(() => {
    if (audioServiceRef.current && examStarted) {
      audioServiceRef.current.setRemainingSeconds(remainingSeconds);
    }
  }, [remainingSeconds, examStarted]);

  // Make navigate function available for end callback
  useEffect(() => {
    window.navigateToDashboard = () => {
      // Check if this is a retry attempt
      const isRetryAttempt = localStorage.getItem('current_task_attempt_id');

      if (isRetryAttempt) {
        console.log('🎯 TASK 3 RETRY - Navigating to Task3ProcessingScreen for retry processing');
        navigate('/task3-processing');
      } else {
        console.log('🎯 TASK 3 - Navigating to combined feedback processing (new comprehensive flow)');
        navigate('/combined-feedback-processing');
      }
    };
    return () => {
      delete window.navigateToDashboard;
    };
  }, [navigate]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      task3UnmountedRef.current = true;
      cancelInstructionToTopicPauseRef.current?.();
      cancelInstructionToTopicPauseRef.current = null;
      if (audioServiceRef.current) {
        audioServiceRef.current.requestShutdown("navigation_dashboard");
      }
      if (speechRecognitionRef.current) {
        speechRecognitionRef.current.cleanup();
      }
    };
  }, []);

  const micStartedRef = useRef(false);

  const startMicIfNotActive = () => {
    if (!micStartedRef.current) {
      // Start voice detection immediately and keep it active
      speechRecognitionRef.current?.start();
      micStartedRef.current = true;
      setIsRecording(true);
      setExamStarted(true);
      setTimerActive(true);
      setStatus("Listening...");
      console.log("🎯 Task 3: Timer and mic started immediately - mic will stay active until exam ends");
    }
  };


  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSecs = seconds % 60;
    return `${minutes}:${remainingSecs.toString().padStart(2, '0')}`;
  };

  //added new for iphone
  const playStaticAudio = async (type: StaticType, onEnd?: () => void) => {
    if (isLiveExamRuntimeExiting()) return;
    const audioObj = getStaticByType(type);
    if (!audioObj) return;

    // Unlock audio context for iOS/Safari
    await audioServiceRef.current?.unlockAudio();

    // Use the Safari-friendly playback method from audioService3
    await audioServiceRef.current?.playStaticAudioUrl(audioObj.url, onEnd);
  };

  const isTask3TopicHandoffCancelled = () =>
    task3UnmountedRef.current || isLiveExamRuntimeExiting();

  const playTask3TopicReadout = (payload: { text: string; audio?: string }) => {
    if (!claimTask3TopicReadout(topicReadoutClaimedRef)) return;

    if (isGuestUser()) {
      const staticTopic = getStaticByType("trail-topic");
      if (!staticTopic) return;
      setTopicText(staticTopic.text);
      setIsAISpeaking(true);
      markSpeakingLatency({ task: 'task3', marker: 'T3_TOPIC_AUDIO_STARTED' });
      void playStaticAudio("trail-topic", () => {
        setIsAISpeaking(false);
        const startExamObj = getStaticByType("start_exam_3");
        if (startExamObj) setIntroText(prev => [...prev, startExamObj.text]);
        setIsAISpeaking(true);
        playStaticAudio("start_exam_3", () => {
          setIsAISpeaking(false);
          setStatus("Listening...");
          startMicIfNotActive();
        });
      });
      return;
    }

    if (payload.audio) {
      setIsAISpeaking(true);
      markSpeakingLatency({ task: 'task3', marker: 'T3_TOPIC_AUDIO_STARTED' });
      (audioServiceRef.current as any)?.playTopicAudio(payload.audio, payload.text, () => {
        setIsAISpeaking(false);
        const startExamObj = getStaticByType("start_exam_3");
        if (startExamObj) setIntroText(prev => [...prev, startExamObj.text]);
        setIsAISpeaking(true);
        playStaticAudio("start_exam_3", () => {
          setIsAISpeaking(false);
          setStatus("Listening...");
          startMicIfNotActive();
        });
      }, () => {
        setTopicText(payload.text || "");
      });
      return;
    }

    const startExamObj = getStaticByType("start_exam_3");
    if (startExamObj) setIntroText(prev => [...prev, startExamObj.text]);
    setIsAISpeaking(true);
    playStaticAudio("start_exam_3", () => {
      setIsAISpeaking(false);
      setStatus("Listening...");
      startMicIfNotActive();
    });
  };

  const releaseTask3TopicIfReady = () => {
    const payload = pendingTopicRef.current;
    if (
      !payload ||
      !shouldReleaseTask3TopicReadout({
        instructionFinished: introFinishedRef.current,
        pauseElapsed: instructionToTopicPauseElapsedRef.current,
        cancelled: isTask3TopicHandoffCancelled(),
        alreadyClaimed: topicReadoutClaimedRef.current,
        hasTopicPayload: true,
      })
    ) {
      return;
    }
    pendingTopicRef.current = null;
    playTask3TopicReadout(payload);
  };

  const playInitialPrompt = () => {
    console.log("Playing Task 3 initial prompt (static)...");

    const introObj = getStaticByType("intro");
    if (introObj) setIntroText(prev => [...prev, introObj.text]);

    setIsAISpeaking(true);
    markSpeakingLatency({ task: 'task3', marker: 'T3_INTRO_AUDIO_STARTED' });

    playStaticAudio("intro", () => {
      markSpeakingLatency({ task: 'task3', marker: 'T3_INTRO_AUDIO_ENDED' });
      setIsAISpeaking(false);
      setStatus("Waiting for topic...");
      introFinishedRef.current = true;
      if (isTask3TopicHandoffCancelled()) return;

      cancelInstructionToTopicPauseRef.current?.();
      instructionToTopicPauseElapsedRef.current = false;
      const { cancel } = scheduleTask3InstructionToTopicPause({
        delayMs: TASK3_INSTRUCTION_TO_TOPIC_PAUSE_MS,
        isCancelled: isTask3TopicHandoffCancelled,
        onElapsed: () => {
          instructionToTopicPauseElapsedRef.current = true;
          releaseTask3TopicIfReady();
        },
      });
      cancelInstructionToTopicPauseRef.current = cancel;
    });
  };


  const handleStartExam = async () => {
    try {
      markSpeakingLatency({ task: 'task3', marker: 'T3_START_FLOW_BEGIN' });
      // Initialize both services in parallel for faster startup (same as Task 1)
      const [audioService, speechService] = await Promise.all([
        // Initialize AudioService
        (async () => {
          const service = new AudioService(
            {
              websocketUrl: import.meta.env.VITE_WEBSOCKET_URL3,
            },
            {
              onStatusChange: (s) => setStatus(s),
              onConnectionChange: (connected) => setIsConnected(connected),
              onTranscriptUpdate: (transcript) => setFullTranscript(transcript),
              onLiveTranscriptUpdate: (transcript) => setLiveTranscript(transcript),

              onAIResponse: handleAIResponse,
              onAIQuestionDisplay: (question) => {
                setAiQuestions(prev => {
                  const incoming = (question || '').trim();
                  const last = (prev[prev.length - 1] || '').trim();
                  if (incoming && incoming === last) return prev;
                  return [...prev, question];
                });
                setUserHasResponded(false);
              },
              onTopicDisplay: (topic) => {
                // Only set topic text from backend for paid users
                if (!isGuestUser()) {
                  setTopicText(topic || "");
                }
              },
              // New: once backend provides topic (with audio), orchestrate playback order:
              // 1) ensure intro was played (already), 2) play topic audio, 3) play start_exam_3, 4) activate mic
              onTopicReceived: async ({ text, audio }) => {
                console.log("🎯 Task 3: onTopicReceived - Received topic from backend:", text, audio);
                pendingTopicRef.current = { text, audio };
                releaseTask3TopicIfReady();
              },
              onAIQuestionHide: () => {
                // Keep history visible; don't remove items
              },
              // When service asks to play predefined end, play end audio then navigate
              onPredefinedEndRequested: async () => {
                console.log('🎙️ Auto-ending: Stopping recording and playing end audio...');

                // Stop recording immediately (will finalize after grace period)
                audioServiceRef.current?.requestShutdown("predefined_end");

                // Play the task completed audio before navigating
                setIsAISpeaking(true);
                playStaticAudio("task_completed", () => {
                  console.log('✅ Task completed audio finished - checking navigation...');
                  setIsAISpeaking(false);

                  // Show divider before navigation
                  setShowDivider(true);

                  setTimeout(() => {
                    // Clean up services
                    audioServiceRef.current?.requestShutdown("predefined_end_cleanup");
                    speechRecognitionRef.current?.cleanup();

                    // Check if this is a retry attempt
                    const isRetryAttempt = localStorage.getItem('current_task_attempt_id');

                    if (isRetryAttempt) {
                      console.log('🎯 TASK 3 RETRY - Navigating to Task3ProcessingScreen for retry processing');
                      navigate('/task3-processing');
                    } else {
                      console.log('🎯 TASK 3 - Navigating to combined feedback processing (new comprehensive flow)');
                      navigate('/combined-feedback-processing');
                    }
                  }, 2000);
                });
              },
              onError: (error) => setStatus(`Error: ${error}`),
              onUserSpeaking: () => {
                setStatus("🗣️ User speaking...");
                setIsUserSpeaking(true);
                if (aiQuestions.length > 0 && !userHasResponded) {
                  setUserHasResponded(true);
                }
              },
              onUserStoppedSpeaking: () => {
                setStatus("🤫 Detecting silence...");
                setIsUserSpeaking(false);
              },
              onMicActivate: () => {
                // Simple microphone activation - just ensure it's active
                console.log("🎯 Task 3: onMicActivate - ensuring microphone stays active");
                if (speechRecognitionRef.current && typeof speechRecognitionRef.current.isActive === 'function') {
                  const isActive = speechRecognitionRef.current.isActive();
                  if (!isActive) {
                    console.log("🔄 onMicActivate - restarting voice detection");
                    speechRecognitionRef.current.start();
                  }
                }
              },
              onMicDeactivate: () => {
                // No need to deactivate mic during AI responses - keep voice detection always active
                console.log("ℹ️ AI speaking - voice detection remains active for user interruption");
              },
              onEndMessageFinished: () => {
                // Show divider after end message plays (for natural timer endings)
                setTimeout(() => setShowDivider(true), 350);
              }
            }
          );

          service.setRemainingSeconds(remainingSeconds);
          return service;
        })(),

        // Initialize DeepgramTranscriptionService
        (async () => {
          return new DeepgramTranscriptionService({
            onInterimResult: (transcript) => {
              setLiveTranscript(transcript);
              audioServiceRef.current?.handleUserSpeech(transcript, "");
            },
            onFinalResult: (transcript) => {
              audioServiceRef.current?.handleUserSpeech("", transcript);
            },
            onError: (error) => setStatus(`Speech Error: ${error}`)
          });
        })()
      ]);

      // Store references
      audioServiceRef.current = audioService;
      // Expose ref globally so other pages/components (navbar, processing screens)
      // can stop recording/cleanup when navigating.
      (window as any).audioServiceRef = audioServiceRef;
      speechRecognitionRef.current = speechService;

      // Start both services in parallel
      const [recordingSuccess, voiceDetectionSuccess] = await Promise.all([
        audioService.startRecording(),
        Promise.resolve(speechService.start())
      ]);

      if (recordingSuccess && voiceDetectionSuccess) {
        console.log("Task 3 Playing intro and requesting topic...");
        // Start immediately
        playInitialPrompt();

        // Pass preferred topic ID if available and then clear it
        const preferredTopicId = localStorage.getItem('preferred_topic_id');
        if (preferredTopicId) {
          localStorage.removeItem('preferred_topic_id');
        }

        // Use goal_level from localStorage for difficultyLevel
        const difficultyLevel = localStorage.getItem('goal_level') || undefined;
        console.log('[Task 3] Sending difficultyLevel from localStorage.goal_level:', difficultyLevel);
        audioServiceRef.current?.requestTask3Topic(preferredTopicId, difficultyLevel);

        console.log('Task 3 exam started successfully');
      } else {
        console.log("Task 3 Failed to start recording or voice detection");
      }
    } catch (error) {
      console.error('Error starting Task 3:', error);
      console.log("Error starting exam");
    }
  };

  useEffect(() => {


    if (!('MediaRecorder' in window)) {
      setStatus("Error: MediaRecorder not supported for Deepgram.");
    }
  }, []);

  // Handle browser back button - redirect to mic-test and stop all audio
  useEffect(() => {
    const handlePopState = (event: PopStateEvent) => {
      event.preventDefault();

      // Stop all audio and recording immediately
      if (audioServiceRef.current) {
        audioServiceRef.current.requestShutdown("popstate_back");
        audioServiceRef.current.cancelAIAudio?.();
      }

      if (speechRecognitionRef.current) {
        speechRecognitionRef.current.cleanup();
      }

      // Stop any local audio elements
      try {
        const audioElements = document.querySelectorAll('audio');
        audioElements.forEach(audio => {
          audio.pause();
          audio.currentTime = 0;
          audio.src = '';
        });
        
        // Stop Web Audio API contexts
        const audioContext = (window as any).__appAudioContext;
        if (audioContext && audioContext.state !== 'closed') {
          audioContext.close().catch(() => { });
        }
      } catch (error) {
        console.warn('Error during audio cleanup on back button:', error);
      }

      // Redirect to mic-test page
      navigate('/mic-test', { replace: true });
    };

    // Add event listener
    window.addEventListener('popstate', handlePopState);
    // Push a state to prevent immediate back navigation
    window.history.pushState(null, '', window.location.href);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [navigate]);

  const handleFinishTask = async () => {
    // Prevent multiple calls - check isFinishingTask
    if (isFinishingTask) {
      console.log('⚠️ TASK 3: handleFinishTask already called - ignoring duplicate call');
      return;
    }

    // Immediately set flag to prevent multiple clicks
    setIsFinishingTask(true);
    console.log('🎯 TASK 3: handleFinishTask called - ending with delayed cleanup');

    // 🔊 UNLOCK AUDIO CONTEXT FOR IOS
    try {
      const AC = (window as any).AudioContext || (window as any).webkitAudioContext;
      if (AC) {
        let ctx = (window as any).__appAudioContext;
        if (!ctx) {
          ctx = new AC();
          (window as any).__appAudioContext = ctx;
        }
        
        // Always try to resume
        if (ctx.state === 'suspended') {
          await ctx.resume();
        }
        
        // Play silent buffer to force unlock
        const buffer = ctx.createBuffer(1, 1, 22050);
        const source = ctx.createBufferSource();
        source.buffer = buffer;
        source.connect(ctx.destination);
        source.start(0);
        console.log('🔊 Audio context unlocked on Task 3 end, state:', ctx.state);
        
        // Short delay to ensure state propagates
        await new Promise(resolve => setTimeout(resolve, 50));
      }
    } catch (e) {
      console.warn('Failed to unlock audio context:', e);
    }

    // 1️⃣ Immediately stop recording flag but keep processing active
    if (audioServiceRef.current) {
      audioServiceRef.current.stopMicrophoneImmediatelyButKeepProcessing();
    }

    // 2️⃣ Immediately update UI state
    setIsRecording(false);
    setTimerActive(false);

    // 3️⃣ Wait 3 seconds before full cleanup (allowing buffered audio to be processed)
    console.log('⏳ TASK 3: Waiting 3 seconds for audio processing to complete...');
    setTimeout(() => {
      console.log('🛑 TASK 3: 3 seconds elapsed - performing full cleanup now');
      
      // Stop speech recognition (Deepgram)
      if (speechRecognitionRef.current) {
        try {
          console.log('🛑 TASK 3: Stopping speech recognition (Deepgram)');
          if (typeof speechRecognitionRef.current.stop === 'function') {
            speechRecognitionRef.current.stop();
          } else if (typeof speechRecognitionRef.current.cleanup === 'function') {
            speechRecognitionRef.current.cleanup();
          }
        } catch (err) {
          console.error("TASK 3: Error stopping speech recognition:", err);
        }
      }
      
      // Stop recording and close websocket
      if (audioServiceRef.current) {
        console.log('🛑 TASK 3: Requesting shutdown (allowing final transcripts)');
        audioServiceRef.current.requestShutdown("user_finish");
      }
      
      console.log('✅ TASK 3: Full cleanup complete - all processing stopped');

      // 4️⃣ NOW play the task completed audio AFTER cleanup (so it doesn't block transcripts)
      setIsAISpeaking(true);
      playStaticAudio("task_completed", () => {
        console.log('✅ Task completed audio finished - checking navigation...');
        setIsAISpeaking(false);

        // Show divider before navigation
        setShowDivider(true);

        setTimeout(() => {
          // Clean up services
          audioServiceRef.current?.requestShutdown("user_finish_final_cleanup");
          speechRecognitionRef.current?.cleanup();

          // Check if this is a retry attempt
          const isRetryAttempt = localStorage.getItem('current_task_attempt_id');

          if (isRetryAttempt) {
            console.log('🎯 TASK 3 RETRY - Navigating to Task3ProcessingScreen for retry processing');
            navigate('/task3-processing');
          } else {
            console.log('🎯 TASK 3 - Navigating to combined feedback processing (new comprehensive flow)');
            navigate('/combined-feedback-processing');
          }
        }, 800);
      });
    }, 3000); // 3 second delay
  };

  const handleExitExam = () => {
    setShowExitModal(true);
  };

  const handleConfirmExit = () => {
    markLiveExamRuntimeExiting();
    cancelInstructionToTopicPauseRef.current?.();
    cancelInstructionToTopicPauseRef.current = null;
    setShowExitModal(false);

    // Stop all static audio / topic / AI
    // Stop audio service & speech recognition
    if (audioServiceRef.current) {
      try {
        audioServiceRef.current.requestShutdown("confirm_exit");
        // If you have any internal stop method for topic/AI audio
        (audioServiceRef.current as any)?.stopTopicAudio?.();
        (audioServiceRef.current as any)?.cancelAIAudio?.();
      } catch { }
    }

    if (speechRecognitionRef.current) {
      try {
        speechRecognitionRef.current.cleanup();
      } catch { }
    }

    // Extra: stop any <audio> elements still in DOM
    try {
      const audioElements = document.querySelectorAll('audio');
      audioElements.forEach(audio => {
        audio.pause();
        audio.currentTime = 0;
        audio.src = '';
      });

      // Stop any Web Audio API contexts if they exist
      const audioContext = (window as any).__appAudioContext;
      if (audioContext && audioContext.state !== 'closed') {
        audioContext.close().catch(() => { });
      }
    } catch (error) {
      console.warn('Error during audio cleanup:', error);
    }

    // Reset all state
    setIsRecording(false);
    setExamStarted(false);
    setRemainingSeconds(270);
    remainingSecondsRef.current = 270;
    setIsAISpeaking(false);
    setAiQuestions([]);
    setFullTranscript("");
    setLiveTranscript("");
    setUserHasResponded(false);
    setIsUserSpeaking(false);
    setTopicText("");
    setStatus("Ready to start");
    setShowDivider(false);

    // Navigate to dashboard
    setTimeout(() => {
      const isAuthenticated = !!localStorage.getItem("access_token");
      if (isAuthenticated) {
        window.location.href = '/dashboard';
      } else {
        window.location.href = 'https://prep2pass.ca';
      }
    }, 100);
  };


  const handleContinueExam = () => {
    setShowExitModal(false);
  };

  return (
    <main id="exam" className="exam-main" aria-label="TCF Speaking Exam Task 3">
      <header className="exam-header">
        <div className="header-inner">
          <div className="header-left">
            <span className="task-badge" aria-current="step">          
              {isRetryAttempt ? "Tâche 3" : "Tâche 3 sur 3"}
            </span>
          </div>

          <div className="header-right" role="group" aria-label="Status">
            <time
              className={`timer ${remainingSeconds === 0 ? 'freeze' : timerActive ? 'active' : 'idle'}`}
              aria-live="polite"
              aria-atomic="true"
              dateTime={`PT${remainingSeconds}S`}
            >
              {formatTime(remainingSeconds)}
            </time>

            <span className="recording" role="status" aria-live="polite">
              <span className="record-dot" aria-hidden="true" />
              <span className="record-text">
                {isRecording ? "Microphone Active" : "Microphone Inactive"}
              </span>
              <span
                className={`record-bars ${isRecording ? "animate" : ""}`}
                aria-hidden="true"
              >
                <i /><i /><i /><i /><i />
              </span>
            </span>
          </div>

          <button
            type="button"
            className="exit-btn"
            onClick={handleExitExam}
            aria-label="Exit Exam"
          >
            Quitter l’examen
          </button>
        </div>
      </header>

      <section className="content">

        <div
          className="ai-messages"
          aria-live="polite"
          style={{ display: 'flex', flexDirection: 'column', gap: '12px', alignItems: 'flex-start' }}
          ref={messagesRef}
        >
          <div
            className="ai-bubble"
            style={{ display: 'flex', gap: '5px', flexDirection: 'column', alignItems: 'flex-start' }}
          >
            {
              introText.length > 0 ? (
                [...introText, ...aiQuestions].map((msg, idx) => (
                  <div key={idx} className="ai-qus-wrapper">
                    <div className="ai-left">
                      <img src={logo} alt="Logo" className="ai-icon" />
                      <strong className="ai-label">AI:</strong>
                    </div>
                    <p
                      className={`text-ai ${idx < introText.length + aiQuestions.length - 1
                        ? 'text-gray dim'
                        : 'fade-in'
                        }`}
                    >
                      {msg}
                    </p>
                  </div>
                ))
              ) : (
                <div className="ai-qus-wrapper ai-qus-wrapper--shell" aria-hidden="true" />
              )
            }
          </div>
        </div>

        <div className={`prompt-card ${isRecording ? "is-recording" : "is-idle"}`} role="region" aria-label="Task 3 Prompt">
          <div>
            <p className="prompt-title">{topicText ? topicText : "Préparation de la tâche 3 ..."}</p>
          </div>
           <div className={`card-wave ${isWaveActive ? "active" : "idle"}`} aria-hidden="true">
              {waveData.map((amp, i) => (
                <span
                  key={i}
                  style={{
                    transform: `scaleY(${isWaveActive ? amp : 1})`
                  }}
                />
              ))}
            </div>
          </div>

      </section>
      <div className="finish-button-container">
        {isRecording && timerActive && (
          <div className="w-full flex justify-end">
            <button 
              className="finish-btn" 
              onClick={handleFinishTask}
              disabled={isFinishingTask}
              style={{ 
                opacity: isFinishingTask ? 0.6 : 1,
                cursor: isFinishingTask ? 'not-allowed' : 'pointer'
              }}
            >
              Terminer la tâche
            </button>
          </div>
        )}
      </div>
      <ExitExamDialog
        open={showExitModal}
        onOpenChange={(open) => {
          setShowExitModal(open);
        }}
        onContinue={handleContinueExam}
        onConfirm={handleConfirmExit}
      />

      {/* {showDivider && (
        <div className="divider-overlay" aria-hidden="true">
          <span className="divider-chip">Exam Completed</span>
        </div>
      )} */}
    </main>
  );
};

export default TaskThree;